import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  HeartIcon,
  ChatBubbleLeftEllipsisIcon,
  InformationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

export const EncounterNavigationPage: React.FC = () => {
  const navigate = useNavigate();

  const handleCommunicationChoice = (needsCommunication: boolean) => {
    if (needsCommunication) {
      // Connect to Phase 3 Patient Communication Portal
      navigate('/onecare/patients/feedback');
    } else {
      // Show encounter information only
      navigate('/onecare/patients/encounter-info');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <button 
              onClick={() => navigate('/onecare/')}
              className="flex items-center group"
            >
              <div className="flex-shrink-0">
                <div className="h-12 w-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                  <HeartIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 group-hover:text-green-600 transition-colors">
                  Aimepic OneCare
                </h1>
                <p className="text-sm text-gray-800">Hospital Encounter</p>
              </div>
            </button>
            
            <nav className="flex space-x-8">
              <button 
                onClick={() => navigate('/onecare/patients')}
                className="flex items-center text-gray-900 hover:text-green-600 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Options
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Hospital Encounter
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">
              How can we assist you?
            </span>
          </h1>
          <p className="text-xl text-gray-900 max-w-3xl mx-auto">
            You mentioned you're here about a current or previous hospital encounter. 
            Do you need to communicate with our hospital staff?
          </p>
        </div>

        <div className="grid gap-8 max-w-4xl mx-auto">
          {/* Need Communication */}
          <button
            onClick={() => handleCommunicationChoice(true)}
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-left transform hover:-translate-y-1 border border-gray-100 hover:border-green-200"
          >
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <ChatBubbleLeftEllipsisIcon className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                  Yes, I need to communicate with hospital staff
                </h3>
                <p className="text-gray-900 text-base leading-relaxed mb-4">
                  I have questions, concerns, complaints, or need to provide feedback about my hospital encounter. 
                  I would like to connect with the appropriate hospital staff member.
                </p>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2">This will connect you to our Patient Communication Portal where you can:</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Submit feedback about your care experience</li>
                    <li>• Ask questions about your treatment or bill</li>
                    <li>• File complaints or concerns</li>
                    <li>• Communicate directly with relevant staff</li>
                  </ul>
                </div>
              </div>

              <div className="flex-shrink-0">
                <svg 
                  className="w-6 h-6 text-gray-700 group-hover:text-green-600 group-hover:translate-x-1 transition-all duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </button>

          {/* Information Only */}
          <button
            onClick={() => handleCommunicationChoice(false)}
            className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 text-left transform hover:-translate-y-1 border border-gray-100 hover:border-blue-200"
          >
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <InformationCircleIcon className="w-8 h-8 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  No, I just need information about my encounter
                </h3>
                <p className="text-gray-900 text-base leading-relaxed mb-4">
                  I'm looking for general information, resources, or details about my hospital encounter. 
                  I don't need to communicate with staff at this time.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">This will show you information about:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Understanding your discharge instructions</li>
                    <li>• Accessing medical records</li>
                    <li>• Billing and insurance information</li>
                    <li>• Follow-up care resources</li>
                  </ul>
                </div>
              </div>

              <div className="flex-shrink-0">
                <svg 
                  className="w-6 h-6 text-gray-700 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </button>
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-gray-800 mb-4">
            Need immediate medical emergency assistance? 
          </p>
          <a 
            href="tel:+15559112273" 
            className="inline-flex items-center font-semibold text-red-600 hover:text-red-700 transition-colors"
          >
            Call (555) 911-CARE immediately
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-800">
              <HeartIcon className="h-4 w-4 mr-2" />
              <span>HIPAA-Ready & Secure</span>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-gray-800">
              <button
                onClick={() => window.open('/patient/privacy', '_blank')}
                className="hover:text-green-600 transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => window.open('/patient/help', '_blank')}
                className="hover:text-green-600 transition-colors"
              >
                Help & Support
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EncounterNavigationPage;